// Vue "Arbre" : arbre descendant d'UN client (client > serveurs > environnements), zoom/pan d3,
// puis modules de l'environnement selectionne en cartes.
import * as d3 from "d3";
import React, { useEffect, useMemo, useRef, useState } from "react";

import useResize from "../../hooks/useResize";
import { BulleFlottante, useBulle } from "../Logs/BulleFlottante";
import { tronquer } from "../Logs/VueTreemap";
import { InfosEnv } from "./StatutsCarto";
import { compteursEnv, formaterDateHeure, libelleClient, libelleServeur, niveauEnv } from "./cartographieDonnees";
import ModuleCarte from "./ModuleCarte";
import Orchestration from "./Orchestration";
import OrchestrationServeur from "./OrchestrationServeur";
import { PLAGE_DEFAUT } from "./frise";
import { useUrlParams } from "../utils/PageEntete";

const liste = (v) => (Array.isArray(v) ? v : []);
const lireId = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
const NAVY = "#012b43";
const BLEU = "#195c84";
const ROUGE = "#e5484d";
const ORANGE = "#ff8801";
const ACCENT = "#ff8801";

const ENV_W = 170;
const ENV_H = 26;
const ENV_GAP = 6;
const SPINE = 14; // epine a 14 px a gauche des environnements (tic horizontal)
const COL_GAP = 16;
const SRV_GAP = 40;
const MAX_PAR_COLONNE = 25;
const CLIENT_W = 240;
const SRV_W = 190;
const CLIENT_H = 48;
const SRV_H = 44;
const NIVEAU_GAP = 56; // client -> serveurs (bus a mi-distance, 28 px de chaque noeud)
const ENVS_GAP = 20;
const COULEUR_ALERTE = "#ff9b9e";
const COULEUR_ECART = "#ffb457";

// "N env. · X ⚠ · Y écarts" : X modules sans source, Y autres ecarts (orange/rouge clair si > 0).
function compteursParts(nbEnv, sans, ecarts) {
  return [
    { t: `${nbEnv} env. · ` },
    { t: `${sans} ⚠`, couleur: sans > 0 ? COULEUR_ALERTE : null },
    { t: " · " },
    { t: `${ecarts} écarts`, couleur: ecarts > 0 ? COULEUR_ECART : null },
  ];
}

// Disposition : client en haut (2 lignes), serveurs en rangee reliés par un bus orthogonal, puis sous
// chaque serveur ses environnements empiles en colonne (sous-colonnes au-dela de MAX_PAR_COLONNE),
// relies par une epine verticale. Aucun texte hors d'un noeud. `segments` : traits orthogonaux.
export function disposer(client, compter) {
  const noeuds = [];
  const segments = [];
  const serveurY = CLIENT_H + NIVEAU_GAP;
  const bas = serveurY + SRV_H;
  const envsY = bas + ENVS_GAP;
  const pas = SPINE + ENV_W + COL_GAP; // pas entre deux epines (sous-colonnes)
  let x = 0;
  let maxLignes = 0;
  let nbEnv = 0;
  let nbSans = 0;
  let nbEcarts = 0;
  const serveurs = [];
  liste(client.servers).forEach((s) => {
    const envs = liste(s.environments);
    const nbCol = Math.max(1, Math.ceil(envs.length / MAX_PAR_COLONNE));
    maxLignes = Math.max(maxLignes, Math.min(envs.length, MAX_PAR_COLONNE));
    const centre = x + SRV_W / 2;
    // largeur du bloc : le serveur, ou le debord a droite des environnements si plus large
    const droite = envs.length > 1 ? centre + (nbCol - 1) * pas + SPINE + ENV_W : x + SRV_W;
    const wBloc = Math.max(SRV_W, droite - x);
    const cpt = envs.reduce(
      (a, e) => {
        const c = compter(e);
        return { sans: a.sans + c.sans, ecarts: a.ecarts + c.ecarts };
      },
      { sans: 0, ecarts: 0 }
    );
    const sans = cpt.sans;
    nbEnv += envs.length;
    nbSans += sans;
    nbEcarts += cpt.ecarts;
    const srv = {
      type: "server",
      serverId: s.id === undefined ? null : s.id,
      nom: libelleServeur(s),
      x,
      y: serveurY,
      w: SRV_W,
      h: SRV_H,
      lignes: [
        { parts: [{ t: tronquer(libelleServeur(s), SRV_W - 16) }], y: SRV_H * 0.36, taille: 13, gras: true },
        {
          parts: compteursParts(envs.length, sans, cpt.ecarts),
          y: SRV_H * 0.74,
          taille: 11,
        },
      ],
    };
    noeuds.push(srv);
    serveurs.push(srv);
    const envNoeud = (e, ex, ey) => ({
      type: "env",
      env: e,
      nom: `${e.code}${e.label ? ` · ${e.label}` : ""}`,
      x: ex,
      y: ey,
      w: ENV_W,
      h: ENV_H,
    });
    if (envs.length === 1) {
      // un seul environnement : centre sous le serveur, trait vertical droit
      noeuds.push(envNoeud(envs[0], centre - ENV_W / 2, envsY));
      segments.push({ x1: centre, y1: bas, x2: centre, y2: envsY });
    } else if (envs.length > 1) {
      const fins = [];
      envs.forEach((e, i) => {
        const col = Math.floor(i / MAX_PAR_COLONNE);
        const ey = envsY + (i % MAX_PAR_COLONNE) * (ENV_H + ENV_GAP);
        const sx = centre + col * pas;
        noeuds.push(envNoeud(e, sx + SPINE, ey));
        segments.push({ x1: sx, y1: ey + ENV_H / 2, x2: sx + SPINE, y2: ey + ENV_H / 2 });
        fins[col] = ey + ENV_H / 2;
      });
      const busY = bas + 8;
      fins.forEach((fin, col) => {
        // 1re epine : alignee sur le centre du serveur (depuis son bord bas) ; les autres depuis le bus
        segments.push({ x1: centre + col * pas, y1: col === 0 ? bas : busY, x2: centre + col * pas, y2: fin });
      });
      if (nbCol > 1) segments.push({ x1: centre, y1: busY, x2: centre + (nbCol - 1) * pas, y2: busY });
    }
    x += wBloc + SRV_GAP;
  });
  // Alignement par CENTRES : client au milieu des centres extremes des serveurs (ou sur le centre
  // du serveur unique), jamais sur l'emprise des blocs serveur + colonne d'environnements.
  const centres = serveurs.map((v) => v.x + v.w / 2);
  const cx = centres.length ? (centres[0] + centres[centres.length - 1]) / 2 : CLIENT_W / 2;
  noeuds.push({
    type: "client",
    nom: libelleClient(client),
    x: cx - CLIENT_W / 2,
    y: 0,
    w: CLIENT_W,
    h: CLIENT_H,
    lignes: [
      { parts: [{ t: tronquer(libelleClient(client), CLIENT_W - 16, 7.5) }], y: CLIENT_H * 0.36, taille: 14, gras: true },
      {
        parts: compteursParts(nbEnv, nbSans, nbEcarts),
        y: CLIENT_H * 0.74,
        taille: 11,
      },
    ],
  });
  if (serveurs.length) {
    const busY = CLIENT_H + NIVEAU_GAP / 2;
    segments.push({ x1: cx, y1: CLIENT_H, x2: cx, y2: busY });
    if (centres.length > 1) segments.push({ x1: centres[0], y1: busY, x2: centres[centres.length - 1], y2: busY });
    serveurs.forEach((v, i) => segments.push({ x1: centres[i], y1: busY, x2: centres[i], y2: serveurY }));
  }
  // decale tout l'arbre : plus rien a gauche de MARGE (le client peut ne plus etre centre)
  const MARGE = 16;
  const minX = Math.min(...noeuds.map((n) => n.x), ...segments.map((t) => Math.min(t.x1, t.x2)));
  const dx = MARGE - minX;
  noeuds.forEach((n) => {
    n.x += dx;
  });
  segments.forEach((t) => {
    t.x1 += dx;
    t.x2 += dx;
  });
  const maxX = Math.max(...noeuds.map((n) => n.x + n.w));
  return {
    noeuds,
    segments,
    liens: segments.map((t) => `M${t.x1},${t.y1}L${t.x2},${t.y2}`),
    nbEnv,
    nbSans,
    nbEcarts,
    largeur: maxX + MARGE,
    hauteur: envsY + maxLignes * (ENV_H + ENV_GAP) + 20,
  };
}

export default function VueArbre({ client }) {
  // Selection (URL : ?env= / ?server=), exclusive : un serveur OU un environnement.
  const [params, majUrl] = useUrlParams();
  const [plage, setPlage] = useState(PLAGE_DEFAUT); // filtre d'heures conserve d'une selection a l'autre
  const envId = lireId(params.env);
  const serverId = lireId(params.server);
  const majUrlRef = useRef(majUrl);
  majUrlRef.current = majUrl;
  const setEnvId = (id) => majUrlRef.current({ env: id, server: null });
  const setServerId = (id) => majUrlRef.current({ server: id, env: null });
  const conteneur = useRef();
  const svgRef = useRef();
  const size = useResize(conteneur);
  const { bulle, montrer, cacher } = useBulle();
  const montrerRef = useRef(montrer);
  const cacherRef = useRef(cacher);
  montrerRef.current = montrer;
  cacherRef.current = cacher;

  const envSel = useMemo(() => {
    if (!client) return null;
    for (const s of liste(client.servers)) {
      const e = liste(s.environments).find((x) => x.id === envId);
      if (e) return e;
    }
    return null;
  }, [client, envId]);
  const serverSel = useMemo(() => {
    if (!client || serverId === null) return null;
    return liste(client.servers).find((s) => s.id === serverId) || null;
  }, [client, serverId]);
  const envIdRef = useRef(envId);
  envIdRef.current = envId;
  const serverIdRef = useRef(serverId);
  serverIdRef.current = serverId;

  const largeur = (size && size.width) || 900;

  useEffect(() => {
    if (!client || !svgRef.current) return;
    const dispo = disposer(client, compteursEnv);
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", largeur).attr("height", dispo.hauteur);
    const g = svg.append("g");
    g.append("g")
      .attr("fill", "none")
      .attr("stroke", "#c3cbd5")
      .attr("stroke-width", 1.5)
      .selectAll("path")
      .data(dispo.liens)
      .join("path")
      .attr("d", (d) => d);

    const n = g
      .selectAll("g.noeud")
      .data(dispo.noeuds)
      .join("g")
      .attr("class", (d) => `noeud noeud-${d.type}`)
      .attr("transform", (d) => `translate(${d.x},${d.y})`);

    n.each(function (d) {
      const gn = d3.select(this);
      let fond = d.type === "client" ? NAVY : BLEU;
      let texte = "#fff";
      if (d.type === "env") {
        const niveau = niveauEnv(d.env);
        fond = niveau === "rouge" ? ROUGE : niveau === "orange" ? ORANGE : "#e4e8ee";
        texte = niveau === "gris" ? "#1d2733" : "#fff";
      }
      gn.append("rect")
        .attr("class", d.type === "env" ? "env-rect" : "cadre")
        .attr("width", d.w)
        .attr("height", d.h)
        .attr("rx", 6)
        .attr("fill", fond)
        .attr(
          "stroke",
          (d.type === "env" && d.env.id === envIdRef.current) ||
            (d.type === "server" && d.serverId !== null && d.serverId === serverIdRef.current)
            ? ACCENT
            : "none"
        )
        .attr("stroke-width", 3);
      if (d.lignes) {
        d.lignes.forEach((l) => {
          const t = gn
            .append("text")
            .attr("x", d.w / 2)
            .attr("y", l.y)
            .attr("text-anchor", "middle")
            .attr("dy", "0.35em")
            .attr("font-size", l.taille)
            .attr("font-weight", l.gras ? 700 : 400)
            .attr("fill", texte);
          l.parts.forEach((p) =>
            t
              .append("tspan")
              .attr("fill", p.couleur || texte)
              .attr("font-weight", p.couleur ? 700 : null)
              .text(p.t)
          );
        });
      } else {
        gn.append("text")
          .attr("x", 8)
          .attr("y", d.h / 2)
          .attr("dy", "0.35em")
          .attr("font-size", 12)
          .attr("fill", texte)
          .text(tronquer(d.nom, d.w - 14));
      }
      if (d.type === "server" && d.serverId !== null) {
        gn.attr("data-server-id", d.serverId)
          .style("cursor", "pointer")
          .on("click", () => setServerId(d.serverId));
      }
      if (d.type === "env") {
        const e = d.env;
        gn.attr("data-env-id", e.id)
          .style("cursor", "pointer")
          .on("click", () => setEnvId(e.id))
          .on("mousemove", (ev) =>
            montrerRef.current(
              ev,
              <div>
                <b>{e.code}</b>
                {e.label ? ` — ${e.label}` : ""}
                <br />
                {formaterDateHeure(e.settings_changed_at)
                  ? `Settings modifiés le ${formaterDateHeure(e.settings_changed_at)}`
                  : "aucun relevé de settings"}
                <br />
                {formaterDateHeure(e.last_activity_at)
                  ? `Dernière activité le ${formaterDateHeure(e.last_activity_at)}`
                  : "aucune activité sur 30 j"}
              </div>
            )
          )
          .on("mouseleave", () => cacherRef.current());
      }
    });

    // Zoom initial a 1 ; molette = defilement de la page (zoom avec Ctrl/Cmd + molette), glisser = pan.
    const zoom = d3
      .zoom()
      .extent([[0, 0], [largeur, dispo.hauteur]])
      .scaleExtent([0.2, 3])
      .filter((ev) => (ev.type !== "wheel" || ev.ctrlKey || ev.metaKey) && !ev.button)
      .on("zoom", (ev) => g.attr("transform", ev.transform));
    svg.call(zoom);
    const tx = dispo.largeur < largeur ? (largeur - dispo.largeur) / 2 : 10;
    svg.call(zoom.transform, d3.zoomIdentity.translate(tx, 10));
  }, [client, largeur]);

  // Selection : contour accentue sans reconstruire (ni reinitialiser le zoom) + defilement vers les cartes.
  useEffect(() => {
    if (!svgRef.current) return;
    d3.select(svgRef.current)
      .selectAll("g.noeud-env")
      .select("rect")
      .attr("stroke", (d) => (d.env.id === envId ? ACCENT : "none"));
    d3.select(svgRef.current)
      .selectAll("g.noeud-server")
      .select("rect")
      .attr("stroke", (d) => (d.serverId !== null && d.serverId === serverId ? ACCENT : "none"));
  }, [envId, serverId, client]);
  const clientCourant = client ? client.id : null;
  const clientPrecedent = useRef(clientCourant);
  useEffect(() => {
    // changement de client : la selection ne vaut plus (pas a l'ouverture, un lien peut la porter)
    if (clientPrecedent.current !== clientCourant) majUrlRef.current({ env: null, server: null });
    clientPrecedent.current = clientCourant;
  }, [clientCourant]);
  const modulesRef = useRef();
  useEffect(() => {
    if ((envId !== null || serverId !== null) && modulesRef.current && modulesRef.current.scrollIntoView) {
      modulesRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [envId, serverId]);

  if (!client) return <div className="carto-vide">Aucun résultat pour ces filtres</div>;

  return (
    <div data-testid="vue-arbre">
      <div className="carto-arbre-svg" ref={conteneur}>
        <svg ref={svgRef} role="img" aria-label="Arbre du client" />
        <BulleFlottante bulle={bulle} />
      </div>
      <div className="carto-modules" data-testid="modules-env" ref={modulesRef}>
        {serverSel ? (
          <OrchestrationServeur
            key={serverSel.id}
            serverId={serverSel.id}
            libelle={libelleServeur(serverSel)}
            plage={plage}
            onPlage={setPlage}
          />
        ) : !envSel ? (
          <div className="carto-sub">
            Cliquez sur un environnement pour voir ses modules, ou sur un serveur pour voir son orchestration.
          </div>
        ) : (
          <>
            <h3>
              {envSel.code}
              {envSel.label ? ` — ${envSel.label}` : ""}
            </h3>
            <div>
              <InfosEnv env={envSel} />
            </div>
            {liste(envSel.modules).length === 0 ? (
              <div className="carto-sub">aucun module avec source</div>
            ) : (
              <div className="carto-grille">
                {liste(envSel.modules).map((m) => (
                  <ModuleCarte key={m.name || m.code} module={m} />
                ))}
              </div>
            )}
            {liste(envSel.unmapped_flows).length > 0 && (
              <div className="carto-non-rattaches" data-testid="flux-non-rattaches">
                <div className="carto-bloc-titre">Flux non rattachés</div>
                {liste(envSel.unmapped_flows).map((f) => (
                  <div key={f.provider} className="carto-flux">
                    <span className="carto-pill observee" title={liste(f.jobs).join("\n")}>
                      {f.provider}
                    </span>
                    <span className="carto-sub">
                      {f.last_run_at ? ` dernier passage ${formaterDateHeure(f.last_run_at, "DD/MM HH:mm")}` : ""}
                      {liste(f.jobs).length ? ` · ${liste(f.jobs).join(", ")}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <Orchestration envId={envSel.id} plage={plage} onPlage={setPlage} />
          </>
        )}
      </div>
    </div>
  );
}
